import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Spinner } from '../../components/atoms/Spinner'
import { PASSWORD } from '../../../constants/password.constants'
import { STAFFDEFAULTPASSWORD } from '../../../constants/staff.constants'
import { useStaffWelcome } from '../../../viewmodels/auth/useStaffWelcome'

/**
 * /staff/welcome — one-time password reset for invited staff.
 *
 * URL: /staff/welcome?token=<32-byte-base64url>
 *
 *   1. On mount: verify(token) confirms the invite is valid and pulls
 *      the staff's first name for the greeting. Errors render full-page
 *      (no form).
 *   2. Two password inputs. Client-side: must match, must be ≥
 *      MINLENGTH, must not equal the system default.
 *   3. Submit confirm(token, password) — server hashes, flips
 *      status='active', sets the dashboard cookie, returns the landing
 *      path. Navigate there on success.
 */
export function StaffWelcomePage(): JSX.Element {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''
  const { verify, confirm } = useStaffWelcome()

  const [phase, setPhase] = useState<'verifying' | 'form' | 'submitting' | 'expired' | 'error'>('verifying')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [greeting, setGreeting] = useState<string>('')
  const [pw1, setPw1] = useState('')
  const [pw2, setPw2] = useState('')

  useEffect(() => {
    if (token.length === 0) {
      setPhase('expired')
      setErrorMsg('Missing token in the URL. Make sure you clicked the link from the welcome email.')
      return
    }
    const ctrl = new AbortController()
    let cancelled = false
    void (async () => {
      const result = await verify(token, ctrl.signal)
      if (cancelled) return
      if (result.ok) {
        const fn = result.data.firstName ?? result.data.email
        setGreeting(fn)
        setPhase('form')
      } else if (result.message === 'aborted') {
        // Effect torn down before request returned — ignore.
        return
      } else {
        setErrorMsg(result.message)
        setPhase('expired')
      }
    })()
    return () => {
      cancelled = true
      ctrl.abort()
    }
  }, [token, verify])

  function passwordsValid(): { ok: true } | { ok: false; reason: string } {
    if (pw1.length < PASSWORD.MINLENGTH) {
      return { ok: false, reason: `Password must be at least ${PASSWORD.MINLENGTH} characters` }
    }
    if (pw1 !== pw2) {
      return { ok: false, reason: 'Passwords do not match' }
    }
    if (pw1 === STAFFDEFAULTPASSWORD) {
      return { ok: false, reason: 'New password cannot be the same as the temporary one from the email' }
    }
    return { ok: true }
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    const check = passwordsValid()
    if (!check.ok) {
      setErrorMsg(check.reason)
      return
    }
    setPhase('submitting')
    setErrorMsg(null)
    const result = await confirm(token, pw1, pw2)
    if (result.ok) {
      navigate(result.landingPath, { replace: true })
      return
    }
    setErrorMsg(result.message)
    setPhase('form')
  }

  if (phase === 'verifying') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner />
      </div>
    )
  }
  if (phase === 'expired' || phase === 'error') {
    return (
      <div className="max-w-md mx-auto py-12 px-4">
        <h1 className="font-heading text-h2 text-charcoal mb-4">Welcome link</h1>
        <Alert tone="error">{errorMsg ?? 'Something went wrong.'}</Alert>
        <p className="font-body text-body-sm text-text-secondary mt-4">
          Ask your admin to re-issue the welcome invite.
        </p>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto py-12 px-4">
      <h1 className="font-heading text-h2 text-charcoal mb-2">Welcome, {greeting}</h1>
      <p className="font-body text-body-sm text-text-secondary mb-6">
        Set a password for your operator account. The temporary one from your
        invite email won&rsquo;t work after this.
      </p>

      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <label className="block">
          <span className="block text-body-sm font-body text-text-primary mb-1">New password</span>
          <Input
            type="password"
            value={pw1}
            onChange={(e) => setPw1(e.target.value)}
            minLength={PASSWORD.MINLENGTH}
            required
            autoFocus
          />
          <span className="block text-body-xs font-body text-text-secondary mt-1">
            At least {PASSWORD.MINLENGTH} characters. Cannot match the temporary password.
          </span>
        </label>

        <label className="block">
          <span className="block text-body-sm font-body text-text-primary mb-1">Confirm password</span>
          <Input
            type="password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            minLength={PASSWORD.MINLENGTH}
            required
          />
        </label>

        {errorMsg !== null ? <Alert tone="error">{errorMsg}</Alert> : null}

        <Button type="submit" disabled={phase === 'submitting'} className="w-full">
          {phase === 'submitting' ? 'Saving…' : 'Set password & sign in'}
        </Button>
      </form>
    </div>
  )
}
